import assign from 'lodash/assign';
import mixpanel from 'mixpanel-browser';

import { E2E_TEST_USER_SHARED_NAME } from '../../utils/e2e-testing-users';

export enum UserTrackingEvents {
  LOGIN = 'login',
  SESSION = 'session',
  PAGE_VISIT = 'page visit',
  PAGE_LEAVE = 'page_leave',
  OPEN_MENU = 'open menu',
  TAB_CHANGE = 'tab change',
  TOOLTIP_OPEN = 'tooltip open',
}

export enum InitiationTrackingEvents {
  FINISH_TOUR = 'finish tour',
  SKIP_TOUR = 'skip tour',
}

export enum CTAInteractionTrackingEvents {
  START_TRIAL = 'start trial',
  SIGN_UP_SUCCESS = 'sign up success',
  PROVIDE_ONBOARDING_INFO = 'provide onboarding info',
  CONTACT_SALES_CLICK = 'contact sales click',
  RESEND_VERIFICATION_EMAIL = 'resend verification email click',
  TRIAL_UPGRADE = 'new sales reach out trial',
}

export enum FeatureTrackingEvents {
  DATA_DOWNLOAD = 'data download',
  PNG_DOWNLOAD = 'png download',
  PPT_DOWNLOAD = 'ppt download',
  PPT_PREVIEW = 'ppt preview',
  PLOT_LINK_GENERATE = 'plot link generate',
}

// Saved views events – keep separate to avoid bloating "feature" bucket. The
// values keep the feature's old "saved sets" name: Mixpanel reports are built
// on them, and a new value would start a new event with no history.
export enum SavedViewsEvents {
  OPEN = 'saved sets open',
  CREATE = 'saved sets create',
  DELETE = 'saved sets delete',
  RENAME = 'saved sets rename',
  APPLY = 'saved sets apply',
  OVERWRITE = 'saved sets overwrite',
}

export enum SourceWhaleTrackingEvents {
  ONBOARDING_OPEN = 'sourcewhale onboarding open',
  EXPORT_OPEN = 'sourcewhale export open',
  EXPORT_CAMPAIGN = 'sourcewhale export campaign',
  EXPORT_CONTACTS = 'sourcewhale export contacts',
}

export enum TutorialTrackingEvents {
  TUTORIAL_OPENED = 'tutorial opened',
  WATCH_TUTORIAL_VIDEO = 'watched tutorial video',
  END_TUTORIAL_VIDEO = 'end tutorial video',
}

export enum OnboardingTrackingEvents {
  TEST_A_NEWS_BUTTON_CLICK = 'test (a) news button click',
}

export enum ABTestingTrackingEvents {
  TEST_A_SESSION = 'test (a) session',
  TEST_A_SIGNUP = 'test (a) sign up',
  TEST_B_SESSION = 'test (b) session',
  TEST_B_SIGNUP = 'test (b) sign up',
}

export enum BuildADashTrackingEvents {
  CREATE_NEW_PAGE = 'create new page',
  CLONE_PAGE = 'clone page',
  SAVE_PAGE = 'save page',
  EDIT_PAGE = 'edit page',
  VIEW_CUSTOM_PAGE = 'view custom page',
  DELETE_PAGE = 'delete page',
  AI_ASSISTANT_PROMPT_SUBMIT = 'ai assistant prompt submit',
  GLOBAL_AI_ASSISTANT_OPEN = 'global ai assistant open',
  GLOBAL_AI_ASSISTANT_OPTION = 'global ai assistant option selected',
  GLOBAL_AI_ASSISTANT_CHART_APPLIED = 'global ai assistant chart applied',
  GLOBAL_AI_ASSISTANT_PAGE_STREAM_DONE = 'global ai assistant page stream done',
  GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED = 'global ai assistant route filter queued',
  GLOBAL_AI_ASSISTANT_ROUTE_FILTER_APPLIED = 'global ai assistant route filter applied',
  GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED = 'global ai assistant route filter failed',
  GLOBAL_AI_ASSISTANT_REPORT_OFFERED = 'global ai assistant report offered',
  GLOBAL_AI_ASSISTANT_REPORT_CLICKED = 'global ai assistant report clicked',
  GLOBAL_AI_ASSISTANT_REPORT_PREFILL = 'global ai assistant report prefill',
  GLOBAL_AI_ASSISTANT_REPORT_PREFILL_APPLIED = 'global ai assistant report prefill applied',
  GLOBAL_AI_ASSISTANT_NEW_CHAT = 'global ai assistant new chat',
  GLOBAL_AI_ASSISTANT_OUTCOME = 'global ai assistant outcome',
  REPORT_DOCUMENT_LAYOUT_REPAIRED = 'report document layout repaired',
}

// Report builder + data enrichment.
export enum ReportEnrichmentTrackingEvents {
  REPORT_REQUESTED = 'report requested',
  DATA_ENRICHMENT_SUBMITTED = 'data enrichment submitted',
}

// Page navigation, preferences and page-level (whole-page) downloads.
export enum PageNavTrackingEvents {
  NAVIGATION_INITIATED = 'navigation initiated',
  PREFERENCE_CHANGED = 'preference changed',
  PAGE_DOWNLOAD_STARTED = 'page download started',
}

// AI natural-language search vs manual filtering — lets us measure the split.
export enum AiSearchTrackingEvents {
  AI_SEARCH_APPLIED = 'ai search applied',
  FILTERS_APPLIED = 'filters applied',
}

// Inside the filter menus: the destructive clear, the shortcut chips above a
// tree (recents, competitors, a trial plan's companies) and the company info
// tooltip's own add controls.
export enum FilterInteractionTrackingEvents {
  FILTER_RESET = 'filter reset',
  ENTITY_SHORTCUT_ADDED = 'entity shortcut added',
  ENTITY_INFO_INTERACTION = 'entity info interaction',
}

// Taxonomy selection (role vs skill; adaptive vs concrete).
export enum TaxonomyTrackingEvents {
  TAXONOMY_SELECTION_CHANGED = 'taxonomy selection changed',
  // Saved-set or link selections the current taxonomy rejects were dropped.
  TAXONOMY_FILTERS_REMOVED = 'taxonomy filters removed',
}

// Data Builder wizard: lightweight funnel steps (drop-off) + one rich submission event.
export enum DataBuilderTrackingEvents {
  STARTED = 'data builder started',
  STEP_REACHED = 'data builder step reached',
  ESTIMATE_FINISHED = 'data builder estimate finished',
  SUBMITTED = 'data builder submitted',
  SUBMIT_FAILED = 'data builder submit failed',
  // Delivery Status: opening a delivery's details or picking a row action, and
  // the two actions that complete without leaving the page.
  DELIVERY_ACTION = 'data builder delivery action',
  DELIVERY_CONFIG_SAVED = 'data builder delivery config saved',
  RUN_CANCELLED = 'data builder run cancelled',
  // Share links, from the sharer's two steps to the recipient's two.
  SHARE_LINK_GENERATED = 'data builder share link generated',
  SHARE_LINK_COPIED = 'data builder share link copied',
  SHARE_LINK_OPENED = 'data builder share link opened',
  SHARE_LINK_ACCEPTED = 'data builder share link accepted',
}

// Admin impersonation sessions. Mixpanel keeps identifying the admin; these
// events plus the `is_impersonating` super property are what lets the client
// boards exclude that traffic.
export enum ImpersonationTrackingEvents {
  STARTED = 'impersonation started',
  ENDED = 'impersonation ended',
}

// Internal tooling: the health-check board and the Data Inspector.
export enum InternalToolsTrackingEvents {
  HEALTH_CHECK_RUN = 'health check run',
  DATA_INSPECTOR_USED = 'data inspector used',
}

// Talent Discovery and Posting Discovery results: the list (table) / card toggle.
export enum DiscoveryTrackingEvents {
  VIEW_MODE_CHANGED = 'discovery view mode changed',
}

export type MixpanelEvents =
  | UserTrackingEvents
  | InitiationTrackingEvents
  | CTAInteractionTrackingEvents
  | FeatureTrackingEvents
  | SavedViewsEvents
  | ReportEnrichmentTrackingEvents
  | PageNavTrackingEvents
  | AiSearchTrackingEvents
  | FilterInteractionTrackingEvents
  | TaxonomyTrackingEvents
  | SourceWhaleTrackingEvents
  | TutorialTrackingEvents
  | OnboardingTrackingEvents
  | ABTestingTrackingEvents
  | BuildADashTrackingEvents
  | DataBuilderTrackingEvents
  | ImpersonationTrackingEvents
  | InternalToolsTrackingEvents
  | DiscoveryTrackingEvents;

export type MixpanelUser = {
  id: string;
  client_name: string;
  username?: string;
  trial?: boolean;
  trial_day?: number;
  // Profile enrichment — lets the usage boards segment by role, tenure and status.
  /** Access role value, e.g. CLIENT_USER / CLIENT_ADMIN / REVELIO_ADMIN / SUPER_ADMIN. */
  user_role?: string;
  account_created_at?: string;
  /** Granular trial tier while on trial (see TrialType). */
  trial_type?: string;
  is_active?: boolean;
};

export const userTracking = assign(mixpanel, {
  // not typed to avoid circular dependancy by importing from libs/auth
  setUser: (user: MixpanelUser) => {
    // Completely disable tracking for E2E test users
    if (user.username?.includes(E2E_TEST_USER_SHARED_NAME)) {
      mixpanel.opt_out_tracking();
      return;
    }

    // For non-test users, opt in and set user data
    mixpanel.opt_in_tracking();
    mixpanel.identify(user.username);
    mixpanel.people.set(user);
    // Stamp role on every event (not just the profile) so raw event streams stay segmentable.
    mixpanel.register({
      ...(user.user_role !== undefined && { user_role: user.user_role }),
    });
  },
  login: () => mixpanel.track(UserTrackingEvents.LOGIN),
});
